import { Link } from "react-router-dom";
import { useSite } from "../site/SiteContext";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { isPlaceholder } from "../content/services";

export function LogoTile({ logo, logoLight, name, light = false, className = "h-10 max-w-[160px]" }) {
  const src = light && logoLight ? logoLight : logo;
  if (src) {
    return <img src={src} alt={`${name} logo`} className={`${className} object-contain`} loading="lazy" />;
  }
  return (
    <span className={`text-sm font-bold uppercase tracking-widest ${light ? "text-white/70" : "text-navy/50"}`}>
      {isPlaceholder(name) ? "Client" : name}
    </span>
  );
}

function Tags({ services, platforms }) {
  return (
    <div className="flex flex-wrap gap-2">
      {services.filter((s) => !isPlaceholder(s)).map((s) => (
        <span key={s} className="rounded-full bg-navy/5 px-3 py-1 text-xs font-bold text-navy">
          {s}
        </span>
      ))}
      {platforms?.map((p) => (
        <span key={p} className="rounded-full border border-mist px-3 py-1 text-xs font-medium text-navy/60">
          {p}
        </span>
      ))}
    </div>
  );
}

export function CaseCard({ cs, featured = false }) {
  const { casesBase } = useSite();
  const comingSoon = cs.status === "coming-soon";

  if (comingSoon) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.45 }}
        data-testid={`case-card-${cs.slug}`}
        className="flex flex-col justify-between rounded-xl border border-dashed border-mist bg-fog p-6 opacity-80"
      >
        <div>
          <LogoTile logo={cs.logo} name={cs.client} />
          <p className="mt-4 text-base font-bold text-navy/60">
            {isPlaceholder(cs.headline) ? `${isPlaceholder(cs.client) ? "New client" : cs.client} results` : cs.headline}
          </p>
        </div>
        <p className="mt-6 text-xs font-bold uppercase tracking-widest text-navy/40">Case study coming soon</p>
      </motion.div>
    );
  }

  const heroStat = cs.heroStats?.find((s) => !isPlaceholder(s.value));

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45 }}
      data-testid={`case-card-${cs.slug}`}
      className={`group flex flex-col rounded-xl border border-mist bg-white transition-colors duration-200 hover:border-pink ${
        featured ? "md:col-span-2 md:flex-row" : ""
      }`}
    >
      {featured && cs.heroImage && (
        <div className="aspect-[4/5] w-full shrink-0 overflow-hidden rounded-t-xl md:w-[300px] md:rounded-l-xl md:rounded-tr-none">
          <img src={cs.heroImage} alt={`${cs.client} creative`} className="h-full w-full object-cover" loading="lazy" />
        </div>
      )}
      <div className="flex flex-1 flex-col justify-center p-6 md:p-8">
        <LogoTile logo={cs.logo} name={cs.client} />
        <h3 className="mt-4 text-lg font-bold leading-snug text-navy">{cs.headline}</h3>
        {heroStat && (
          <div className="mt-4">
            <span className="text-3xl font-extrabold text-navy">{heroStat.value}</span>
            <span className="ml-2 text-xs font-medium uppercase tracking-widest text-navy/60">{heroStat.label}</span>
          </div>
        )}
        <div className="mt-4">
          <Tags services={cs.services} platforms={cs.platforms} />
        </div>
        <Link
          to={`${casesBase}/${cs.slug}`}
          data-testid={`case-card-${cs.slug}-link`}
          className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-link group-hover:gap-3 transition-all"
        >
          Read the case study <ArrowRight size={16} />
        </Link>
      </div>
    </motion.div>
  );
}
